import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from "react";

import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { useParams } from "@Libs/Router/hooks/useParams";

import {
  AUTH_SCREEN_KEYS,
  STEP_KEYS,
  SUBCATEGORY_SCREEN_KEY,
  isAuthBranchScreenKey,
} from "../config/constants";
import { setLastScreen } from "../hooks/useDraftStorage";
import { useDraftIdentity } from "../hooks/useDraftIdentity";
import {
  useServiceRequestDraft,
  useTaskCreationConfig,
} from "../hooks/useServiceRequestDraft";
import {
  IAnswerMap,
  IFlowScreen,
  IFlowStep,
  IServiceRequestDraft,
  ITaskCreationConfig,
  ITaskCreationMode,
} from "../types";
import {
  buildFlowShape,
  countScreenPositionProgress,
  toAnswerMap,
} from "../utils/buildFlowShape";

export interface ITaskCreationFlowState {
  /** The step groups, in order. Each carries the keys of its screens. */
  steps: IFlowStep[];
  /** Every screen the user will walk, in order. Excludes the description
   * screen, which is outside the steps. */
  screens: IFlowScreen[];
  /** The `screenKey` in the URL. `undefined` on the description screen. */
  currentScreenKey?: string;
  /** The step group the current screen belongs to. */
  currentStep?: IFlowStep;
  /** 1-based position of `currentStep` — the number the header shows. Its
   * array index is this minus one. */
  currentStepNumber?: number;
  /** How many step groups there are: 2 for a logged-in user, 3 otherwise. */
  totalSteps: number;
  /** Required questions answered so far, across every screen (the
   * subcategory picker counts as one). */
  answeredQuestions: number;
  /** Total required questions across every screen, so
   * `answeredQuestions / totalQuestions` is the bar fill. */
  totalQuestions: number;
  /** Every answer so far, keyed by question key. */
  answers: IAnswerMap;
  /** The draft itself, for anything `answers` does not carry (description,
   * photos, address, subcategory). */
  draft?: IServiceRequestDraft;
  /**
   * The draft's own theme, `undefined` until a subcategory is picked. Use
   * `useFlowPCategory` for the resolved one — it ranks the URL and the
   * category tree alongside this.
   */
  pCategoryId: number | undefined;
  /** The config the shape was built from. Absent until a draft exists. */
  config?: ITaskCreationConfig;
  isLoggedIn: boolean;
  /** True while the draft's or the config's first fetch is in flight — never
   * true again on a background refetch, so it is safe to gate a screen's whole
   * body on. */
  isFlowLoading: boolean;
}

const EMPTY_STATE: ITaskCreationFlowState = {
  pCategoryId: undefined,
  steps: [],
  screens: [],
  totalSteps: 0,
  answeredQuestions: 0,
  totalQuestions: 0,
  answers: {},
  isLoggedIn: false,
  isFlowLoading: false,
};

const DEFAULT_TOTAL_QUESTIONS = 8;

/**
 * The steps every flow has, whatever the subcategory turns out to be.
 *
 * The subcategory screen has no config — that is the point of it — so the header
 * would read "step undefined of 0" without a floor to fall back on. Same
 * logged-in rule as the config-driven shape (`buildFlowShape`): contact details
 * is a step a visitor walks and a step a logged-in user does not have.
 */
const floorSteps = (isLoggedIn: boolean): IFlowStep[] => [
  {
    key: STEP_KEYS.SERVICE_DETAILS,
    position: 1,
    screenKeys: [SUBCATEGORY_SCREEN_KEY],
  },
  { key: STEP_KEYS.TIME_AND_LOCATION, position: 2, screenKeys: [] },
  ...(isLoggedIn
    ? []
    : [
        {
          key: STEP_KEYS.CONTACT_DETAILS,
          position: 3,
          screenKeys: [AUTH_SCREEN_KEYS.EMAIL],
        },
      ]),
];

interface IDeriveFlowStateInput {
  config?: ITaskCreationConfig;
  draft?: IServiceRequestDraft;
  screenKey?: string;
  isLoggedIn: boolean;
  /** Defaults to `false`: it only drives the spinner, so callers that assert on
   * derived shape (the unit tests) need not pass it. */
  isFlowLoading?: boolean;
  /** False while the session request is still in flight, when `isLoggedIn` is
   * `false` only because nothing has answered yet. Defaults to `true`. */
  isAuthResolved?: boolean;
  /** Create unless the widget was mounted for an existing request. */
  mode?: ITaskCreationMode;
}

/**
 * The whole flow state, derived in one place.
 *
 * Pure on purpose: everything the header, the progress bar and the summary need
 * comes out of one call, so they cannot compute two disagreeing versions of
 * "which step is this".
 */
export const deriveFlowState = ({
  config,
  draft,
  screenKey,
  isLoggedIn,
  isFlowLoading = false,
  isAuthResolved = true,
  mode = "create",
}: IDeriveFlowStateInput): ITaskCreationFlowState => {
  // No config, but a draft: the subcategory screen, which is by definition
  // reached before a subcategory — and so before a config — exists. Product
  // fixes it in `service_details`, so the chrome can show "step 1 of N" from the
  // fixed steps alone rather than hiding itself until the config lands.
  if (!config) {
    // No `screenKey` means the description screen, which sits outside the steps
    // entirely. Without this guard a refresh there flashes the chrome: a draft
    // from an earlier visit is already in storage and its config has not landed
    // yet, so the floor below would report "step 1", and the shell would paint
    // the summary panel and shove the form 320px right until the config
    // arrived and the step went undefined again.
    // Edit mode has no subcategory picker to show while the config loads, so
    // it waits for it rather than falling through to the floor below.
    if (!draft || !screenKey || mode === "edit") {
      // A reload *on* a step screen, with the draft still in flight: nothing
      // about the flow's shape is known yet, but the chrome's is — the steps
      // below are fixed. Reported so the shell keeps its summary panel and
      // two-column layout instead of collapsing to the description screen's
      // single column and snapping back a moment later.
      //
      // `screens` stays empty on purpose: that is what tells the screen itself
      // it has nothing to render yet.
      if (isFlowLoading && screenKey) {
        // Contact details is only ever *added*, for a confirmed visitor, so an
        // unresolved session takes the logged-in floor: a third row appearing
        // is a smaller jolt than one being taken away, and a session that is
        // already cached resolves before the draft anyway.
        const loadingSteps = floorSteps(isLoggedIn || !isAuthResolved);

        return {
          ...EMPTY_STATE,
          steps: loadingSteps,
          currentStep: loadingSteps[0],
          currentStepNumber: 1,
          totalSteps: loadingSteps.length,
          isLoggedIn,
          isFlowLoading,
        };
      }

      return { ...EMPTY_STATE, isLoggedIn, isFlowLoading };
    }

    const steps = floorSteps(isLoggedIn);

    return {
      steps,
      screens: [
        {
          key: SUBCATEGORY_SCREEN_KEY,
          stepKey: STEP_KEYS.SERVICE_DETAILS,
          source: "static",
          questions: [],
        },
      ],
      currentScreenKey: SUBCATEGORY_SCREEN_KEY,
      currentStep: steps[0],
      currentStepNumber: 1,
      totalSteps: steps.length,
      answeredQuestions: 1,
      totalQuestions: DEFAULT_TOTAL_QUESTIONS,
      answers: toAnswerMap(draft.answers),
      draft,
      pCategoryId: draft.p_category_id ?? undefined,
      isLoggedIn,
      isFlowLoading,
    };
  }

  // The address is never an answer — it rides on the draft's own `location`
  // field (see `utils/toDraftFields.ts`). Seeding it back under the address
  // question's key is what lets `isScreenComplete` see that screen as done;
  // without it the navigation guard would bounce the user back to it forever.
  // The no-config branch above needs none of this: its only screen is the
  // subcategory picker.
  const addressKey = config.steps
    .flatMap((step) => step.screens)
    .flatMap((screen) => screen.questions)
    .find((question) => question.type === "address")?.key;

  // Photo ids are never an answer either — they ride on the draft's own
  // `photo_attachments` field (see `utils/toDraftFields.ts`). Same reasoning
  // and same fix as `addressKey` above: without seeding it back under the
  // picture_upload question's key, the uploader would render as empty on a
  // resumed draft even though the attachments are already on the server.
  const photosKey = config.steps
    .flatMap((step) => step.screens)
    .flatMap((screen) => screen.questions)
    .find((question) => question.type === "picture_upload")?.key;

  const answers = {
    ...toAnswerMap(draft?.answers),
    ...(addressKey && draft?.location ? { [addressKey]: draft.location } : {}),
    ...(photosKey && draft?.photo_attachments
      ? { [photosKey]: draft.photo_attachments.map((photo) => photo.id) }
      : {}),
  };
  const { screens, steps } = buildFlowShape(config, answers, {
    isLoggedIn,
    mode,
  });

  const currentScreen =
    screens.find((screen) => screen.key === screenKey) ??
    // The auth step's branches are not in the shape, so they report its email
    // screen — which is what keeps the chrome on "step 3 of 3".
    (isAuthBranchScreenKey(screenKey)
      ? screens.find((screen) => screen.key === AUTH_SCREEN_KEYS.EMAIL)
      : undefined);
  const stepIndex = currentScreen
    ? steps.findIndex((step) => step.key === currentScreen.stepKey)
    : -1;
  const { answered, total } = countScreenPositionProgress(
    screens,
    currentScreen?.key
  );

  return {
    steps,
    screens,
    currentScreenKey: currentScreen?.key,
    currentStep: stepIndex === -1 ? undefined : steps[stepIndex],
    currentStepNumber: stepIndex === -1 ? undefined : stepIndex + 1,
    totalSteps: steps.length,
    answeredQuestions: answered,
    totalQuestions: total,
    answers,
    draft,
    pCategoryId: draft?.p_category_id ?? undefined,
    config,
    isLoggedIn,
    isFlowLoading,
  };
};

const TaskCreationFlowContext = createContext<
  ITaskCreationFlowState | undefined
>(undefined);

/**
 * Derives the flow state once, at the base route, and hands it to everything
 * below — including anything rendered in the summary modal or panel, since
 * context crosses the modal's portal.
 *
 * It reads the `screenKey` from the URL and the draft and config from their
 * existing react-query keys, so it adds no fetch of its own: the config is
 * already seeded by the description POST and cached with `staleTime: Infinity`.
 * Deriving here rather than having each screen publish upwards means the state
 * cannot go stale when a screen unmounts.
 */
export const TaskCreationFlowProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const identity = useDraftIdentity();
  const { mode, screenFragment } = identity;
  // The mode's own pattern: this provider sits above the routes, so
  // react-router gives it no params and the match is all there is — the create
  // pattern never matches an edit URL, which left every edit step screen with
  // no `currentScreenKey` and nothing to render.
  const { screenKey } = useParams([screenFragment]);
  const { data: draft, isLoading: isDraftLoading } =
    useServiceRequestDraft(identity);
  const { data: config, isLoading: isConfigLoading } = useTaskCreationConfig(
    draft?.s_category_id
  );
  // The draft counts as well as the config: on a reload the config query is
  // still disabled (it is keyed on the draft's `s_category_id`) and so reads
  // "not loading", which let a screen render its empty question grid — Back and
  // Continue and all — before there was anything to answer.
  //
  // `isLoading`, not `isFetching`: a background refetch of an already-cached
  // draft or config must not blank the screen back to its loading state. A
  // disabled query is `idle` in react-query 3, so neither is true on the
  // description screen, which has no draft yet and must render at once.
  const isFlowLoading = isDraftLoading || isConfigLoading;
  // Contact details is a step for a visitor and a redirect for a logged-in user
  // (settled decision 13), which is the whole difference between "of 3" and
  // "of 2". A failed or absent session reads as logged out.
  const { isSuccess: isLoggedIn, isLoading: isUserLoading } = useCurrentUser();

  const value = useMemo(
    () =>
      deriveFlowState({
        config,
        draft,
        screenKey,
        isLoggedIn,
        isFlowLoading,
        isAuthResolved: !isUserLoading,
        mode,
      }),
    [config, draft, screenKey, isLoggedIn, isFlowLoading, isUserLoading, mode]
  );

  // Keep the resume pointer on the screen actually on show. The description
  // POST seeds it once, but nothing moved it afterwards, so a resumed draft
  // always came back to the picker instead of where the visitor stood.
  // Derived from the URL, so it follows every path in: Continue, Back, the
  // browser's own buttons and a pasted link alike.
  //
  // `currentScreenKey`, not the raw `screenKey`: it is the URL's key already
  // checked against the flow shape, so a key the shape does not carry is
  // never written back. Persisting one used to outlive the tab — a
  // subcategory switch strands the URL on the previous config's screen, and
  // saving that key meant every later resume walked straight back into a
  // screen that cannot render (RT-4321).
  useEffect(() => {
    if (value.currentScreenKey) setLastScreen(value.currentScreenKey);
  }, [value.currentScreenKey]);

  return (
    <TaskCreationFlowContext.Provider value={value}>
      {children}
    </TaskCreationFlowContext.Provider>
  );
};

/**
 * The read surface for the flow's progress: step groups, the current step, the
 * screens, the completed count and the accumulated answers, in one object.
 *
 * Any component inside the flow can call this — it needs to know nothing about
 * routing or fetching. Its first consumer is the summary (modal on small
 * screens, panel on desktop), which renders a placeholder body today and reads
 * its rows from here once they are built.
 *
 * ```tsx
 * const { steps, currentStepNumber, answers } = useTaskCreationFlow();
 * ```
 */
export const useTaskCreationFlow = (): ITaskCreationFlowState => {
  const state = useContext(TaskCreationFlowContext);

  if (!state)
    throw new Error(
      "useTaskCreationFlow must be used inside <TaskCreationFlowProvider>"
    );

  return state;
};
